import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react'

import { mensajeDeError } from '@/lib/api'
import type { Candidato } from '@/lib/elevacion'
import { canjearPin, conectarAutorizadorDePin, useElevacion } from '@/lib/elevacion'
import { PinPad } from '@/componentes/ui/PinPad'

/**
 * «Que lo autorice un encargado».
 *
 * Aparece cuando una acción choca contra un permiso que el usuario no tiene, y
 * es la mitad que le faltaba a §1.4: el encargado pone su PIN, se libera ESA
 * acción por sesenta segundos, y queda anotado en la bitácora quién autorizó
 * qué. Sin esta pantalla, la única salida era agregarle el permiso al rol del
 * cajero, que es cómo un local termina con todos los permisos en todos los
 * roles y sin ningún control.
 *
 * Es un `<dialog>` modal por lo mismo que la confirmación: casi siempre sale
 * de adentro de otro diálogo (el de anular una venta, el del movimiento de
 * caja), y un div quedaría por debajo de la capa superior del navegador.
 */
export function DialogoElevacion() {
  const pedido = useElevacion((s) => s.pedido)
  const responder = useElevacion((s) => s.responder)

  const ref = useRef<HTMLDialogElement>(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [canjeando, setCanjeando] = useState(false)

  // A quiénes hay que preguntarle cuál es, cuando ese PIN es de varias
  // personas que además podrían autorizar esto. Se guarda el PIN tipeado para
  // no hacérselo poner de nuevo: ya lo puso bien.
  const [aElegir, setAElegir] = useState<{ pin: string; candidatos: Candidato[] } | null>(null)

  // El mecanismo se conecta al cliente de la API mientras este diálogo esté
  // montado, por lo mismo que el atajo F1 vive en el cajón de ayuda: la
  // capacidad existe donde hay algo que la atienda.
  useEffect(() => conectarAutorizadorDePin(), [])

  // Responder y limpiar van juntos, y en el MANEJADOR y no en un efecto: el
  // PIN es la credencial de otra persona y no tiene por qué quedar en memoria
  // un segundo más de lo necesario, pero limpiarlo desde un efecto es un
  // setState en cascada que el linter rechaza con razón.
  const cerrar = useCallback(
    (grant: string | null) => {
      setPin('')
      setError(null)
      setAElegir(null)
      responder(grant)
    },
    [responder],
  )

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    if (pedido && !dialogo.open) dialogo.showModal()
    if (!pedido && dialogo.open) dialogo.close()
  }, [pedido])

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    const alCancelar = (e: Event) => {
      e.preventDefault()
      cerrar(null)
    }

    dialogo.addEventListener('cancel', alCancelar)
    return () => dialogo.removeEventListener('cancel', alCancelar)
  }, [cerrar])

  const autorizar = async (completo: string, usuarioId?: number) => {
    if (!pedido || canjeando) return

    setCanjeando(true)
    setError(null)

    try {
      const r = await canjearPin(completo, pedido.permiso, usuarioId)

      // Ese PIN es de varias personas que podrían autorizar esto: el servidor
      // no eligió ninguna —anotar la autorización a nombre de quien no
      // autorizó es lo que este control existe para no hacer— y pregunta.
      if (!r.grant) {
        setAElegir({ pin: completo, candidatos: r.candidatos ?? [] })
        setPin('')
        return
      }

      cerrar(r.grant)
    } catch (e) {
      // El motivo lo escribe el servidor y se muestra tal cual: «ese PIN no es
      // de nadie» y «ese PIN es de alguien que tampoco tiene ese permiso» son
      // dos cosas distintas, y la segunda es la que le dice al cajero a quién
      // ir a buscar.
      setError(mensajeDeError(e, 'No se pudo autorizar.'))
      setPin('')
      setAElegir(null)
    } finally {
      setCanjeando(false)
    }
  }

  return (
    <dialog
      ref={ref}
      aria-labelledby="elevacion-titulo"
      className="hidden w-[calc(100vw-2rem)] max-w-[400px] flex-col rounded-xl border border-borde
                 bg-crema p-0 shadow-elevada backdrop:bg-tinta/45 backdrop:backdrop-blur-[2px]
                 animate-entrar open:flex"
      onClick={(e) => {
        if (e.target === ref.current) cerrar(null)
      }}
    >
      {pedido && (
        <>
          <div className="px-5 pb-1 pt-5 text-center">
            <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-verde-tenue text-verde-oscuro">
              <ShieldCheck className="h-[22px] w-[22px]" />
            </span>

            <h2
              id="elevacion-titulo"
              className="text-[16.5px] font-bold leading-snug tracking-[-0.01em] text-tinta"
            >
              Hace falta la autorización de un encargado
            </h2>

            {/* Se nombra el permiso EN CASTELLANO y no la clave: es lo que
                permite ir a buscar a alguien y decirle qué hace falta. */}
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-tinta-tenue">
              {aElegir ? (
                <>
                  Ese PIN es de más de una persona. Tocá{' '}
                  <strong className="font-semibold text-tinta-suave">tu nombre</strong> para que la
                  autorización quede a tu nombre.
                </>
              ) : (
                <>
                  Para{' '}
                  <strong className="font-semibold text-tinta-suave">{pedido.etiqueta}</strong>{' '}
                  hace falta un PIN con ese permiso. Queda registrado quién autorizó.
                </>
              )}
            </p>
          </div>

          <div className="px-5 py-4">
            {/* Dos pasos, y el segundo casi nunca aparece: el PIN dejó de ser
                único —para entrar se elige la persona y después se pone el
                PIN, así que dos empleados con el mismo no se pisan— y lo
                único que queda ambiguo es esto. Aun así, casi siempre uno solo
                de los que comparten el PIN tiene el permiso, y ahí el
                servidor no pregunta nada.

                Y no se vuelve a pedir el PIN: ya lo puso bien, y hacérselo
                tipear de nuevo con el cliente adelante es la fricción que hace
                que el local deje de usar el control. */}
            {aElegir ? (
              <ul className="grid gap-2">
                {aElegir.candidatos.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="btn-secundario btn-toque w-full justify-center"
                      onClick={() => void autorizar(aElegir.pin, c.id)}
                      disabled={canjeando}
                    >
                      {c.nombre}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <PinPad
                valor={pin}
                onCambio={setPin}
                onConfirmar={(completo) => void autorizar(completo)}
                deshabilitado={canjeando}
              />
            )}

            {canjeando && (
              <p className="mt-3 flex items-center justify-center gap-2 text-[13px] text-tinta-tenue">
                <Loader2 className="h-4 w-4 animate-spin" /> Verificando…
              </p>
            )}

            {error && !canjeando && (
              <p className="error mt-3 text-center" role="alert">
                {error}
              </p>
            )}
          </div>

          <footer className="flex items-center justify-between gap-2 border-t border-borde bg-crema-alt/60 px-5 py-3.5">
            <span className="flex items-center gap-1.5 text-[12.5px] text-tinta-tenue">
              <KeyRound className="h-3.5 w-3.5" />
              El PIN no se guarda
            </span>

            <button
              type="button"
              className="btn-fantasma btn-toque"
              onClick={() => cerrar(null)}
              disabled={canjeando}
            >
              Cancelar
            </button>
          </footer>
        </>
      )}
    </dialog>
  )
}
